import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";

import axiosInstance from "@/api/config";
import { getDatasetMetadata } from "@/api/dataset/datasetApi";
import { usePostDatasetAnalysis } from "@/api/dataset/useDatasetAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { CirclePlusIcon, FilePlusIcon, FolderOpenIcon } from "@/assets/icons";
import DatasetList from "@/components/dataset/datasetList/index";
import SpatialAreaInput from "@/components/spatialAnalysis/spatialAreaInput";
import SpatialOutputAnalysis from "@/components/spatialAnalysis/spatialOutputAnalysis";
import { Divider } from "@/components/ui/Divider";
import { Timezone } from "@/constants/dateTime";
import { MESSAGE } from "@/constants/message";
import { getDeepestAdminFilterFirst } from "@/helpers/datasetAttributeHelper";
import { buildFilterAsDatasetPayload } from "@/helpers/filterHelper";
import { removeStartFromUnderscore } from "@/helpers/string";
import { useMap } from "@/hooks/map/core/useMapContext";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import { useConfiguration } from "@/hooks/project/useConfiguration";
import useConfigurationMappers from "@/hooks/project/useConfigurationMapper";
import { useValidationAnalysisConfigInput } from "@/hooks/spatialAnalysis/useValidationAnalysisConfig";
import { useDatadogAnalytics } from "@/hooks/tracker/useDatadogAnalytics";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import useHandleClearView from "@/hooks/ui/useHandleClearView";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { queryClient } from "@/lib/react-query";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import {
  resetDatasetState,
  setDatasetHistory,
  updateDatasetPublishedStatus
} from "@/store/slice/datasetSlice";
import { resetGlobalFilter } from "@/store/slice/globalFilterSlice";
import {
  resetSpatialState,
  setAdminAreaSettingsDataTemp,
  setAdminTreeSettingsTempSelectedIds,
  setCurrRunningAnalysisId,
  setLatestAnalysisId,
  setPolygonSettingsTemp,
  setSpatialState,
  spatialInitialState,
  SpatialState
} from "@/store/slice/spatialSlice";
import {
  resetUIDataConfigExceptSidebarMenu,
  setActiveSpatialAnalysisPanel,
  setShowPolygonPopup,
  toggleAreaAdminPopup,
  toggleCatchmentPopup,
  toggleGlobalFilterPopup,
  toggleShowProjectList
} from "@/store/slice/uiSlice";
import {
  AdminArea,
  AnalysisResultType,
  CustomCatchment,
  DatasetFilter,
  Marker,
  OutputGrid,
  OutputSiteProfiling,
  SpatialAnalysisDatasetFilter
} from "@/types/dataset";
import { MarkerPinItem } from "@/types/map";
import { ProjectFormData } from "@/types/project";
import {
  AdminAreaSettings,
  AreaInputType,
  ECatchmentInputBy,
  ECatchmentType,
  EOutputAnalysisType,
  EOutputGridType,
  IsochroneData,
  ScoringOptions
} from "@/types/spatial";
import { createMarkersFromDataset } from "@/utils/catchment";
import { getRadiusPolygon } from "@/utils/map";
import {
  getNegativeImpactValue,
  getPercentageValue,
  parseWeightValue
} from "@/utils/weightValues";
import { geoJSONArrayToWKT, geoJSONToWKT } from "@/utils/wkt";

import { LokasiAIProvider } from "@lokasi-ai-web-sdk/app";
import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";
import { v4 as uuidv4 } from "uuid";

import { UnlockPremiumModal } from "../dataset/datasetExplorer/UnlockPremiumModal";
import { GlobalFilter } from "../globalFilter";
import NotificationContent from "../notification/notificationContent";
import ButtonMenu, { ButtonMenuItem } from "../ui/buttonMenu";
import CollapseView from "../ui/collapseView";
import Tooltip from "../ui/tooltip";
import DatasetRecommendationAI from "./datasetRecommendationAI";
import SpatialHeader from "./header";
import ProjectForm from "./projectForm";
import ProjectList from "./projectList";
import SaveConfigurationPopup, {
  ConfigurationFormData
} from "./saveConfigurationPopup";
import { buildSpatialAnalysisTracker } from "./tracker";

const TEST_ID_PREFIX = "spatial-analysis-popup";

type Props = {
  onClose: (isSave?: boolean) => void;
  onOpenDatasetExplorer: (value: boolean) => void;
  onOpenDatasetTabularModal: (value: boolean) => void;
  onOpenGlobalFilter: () => void;
  onClearGlobalFilter: () => void;
};

const SpatialAnalysisPopup = ({
  onClose,
  onOpenDatasetExplorer,
  onOpenDatasetTabularModal,
  onOpenGlobalFilter,
  onClearGlobalFilter
}: Props) => {
  const { trackAction } = useDatadogAnalytics();
  const dispatch = useDispatch();
  const {
    handleResetDatasetToHistoryDataset,
    deleteAllFeatures,
    synchronizeFeatures
  } = useMap();
  const { showConfirmation } = useConfirmationModal();
  const { showToast } = useToast();
  const { handleCloseDetailView } = useHandleClearView();
  const {
    getGridConfig,
    mappingAnalysisOutput,
    mappingAreaInput,
    mappingDataset,
    mappingAdmAreaIds
  } = useConfigurationMappers();

  const [projectNameExist, setProjectNameExist] = useState(false);
  const [generateResultClicked, setGenerateResultClicked] = useState(false);

  const {
    catchmentSettings,
    adminAreaSettings,
    polygonSettings,
    currRunningAnalysisId
  } = useSelector((state: RootState) => state.spatial);
  const {
    showProjectList,
    showCatchmentPopup,
    showAreaAdminPopup,
    showPolygonPopup
  } = useSelector((state: RootState) => state.ui);
  const { datasets, weightValues, filters, rawDatasets, visualizationOrder } =
    useSelector((state: RootState) => state.dataset);

  const spatial = useSelector((state: RootState) => state.spatial);
  const globalFilter = useSelector((state: RootState) => state.globalFilter);

  const { data: userProfile } = useGetUserProfileAPI();

  const {
    isAnalysisJobListEnabled,
    isNewSetupWeightOptionsEnabled,
    isGlobalFilterEnabled,
    isAdminTreeEnabled,
    isDatasetRecommendationAIEnabled
  } = useAppFeatures();

  const { validationErrors, canGenerateAnalysisResults, hasAvailableDataset } =
    useValidationAnalysisConfigInput();

  const { mutateSaveConfig } = useConfiguration({
    onSaveConfigSuccess: () => setShowSaveConfig(false),
    onSaveConfigError: (error) => setProjectNameExist(error.code === 409),
    onOpenProjectFromToast: () => {
      dispatch(toggleShowProjectList(true));
    }
  });

  const { mutate: postDatasetAnalysis, isPending: isLoadingPostAnalysis } =
    usePostDatasetAnalysis({
      onSuccess: (data, payload) => {
        trackAction(
          "run_spatial_analysis",
          buildSpatialAnalysisTracker(payload)
        );

        if (isAnalysisJobListEnabled) {
          dispatch(resetUIDataConfigExceptSidebarMenu());
          if (data?.message) {
            showToast({
              type: EToastType.WARNING,
              message: (
                <NotificationContent
                  title="Queue Limit Reached"
                  description={data.message}
                />
              )
            });
          } else {
            // go to src/pages/home/index.tsx:71 to fetch analysis status
            dispatch(setCurrRunningAnalysisId(data.id));
            dispatch(setLatestAnalysisId(data.id));
            dispatch(
              setDatasetHistory({
                datasets: datasets,
                visualizationOrder: visualizationOrder,
                rawDatasets: rawDatasets
              })
            );
          }
        } else {
          // go to src/pages/home/index.tsx:71 to fetch analysis status
          dispatch(setCurrRunningAnalysisId(data.id));
          dispatch(setLatestAnalysisId(data.id));
          dispatch(
            setDatasetHistory({
              datasets: datasets,
              visualizationOrder: visualizationOrder,
              rawDatasets: rawDatasets
            })
          );
        }
      },
      onError: (error, payload) => {
        trackAction(
          "run_spatial_analysis",
          buildSpatialAnalysisTracker({ ...payload, error })
        );
        showToast({
          type: EToastType.ERROR,
          message: (
            <NotificationContent
              title="Something Went Wrong"
              description="We’re sorry, we cannot proceed with your request. Please try again."
            />
          )
        });
      },
      onSettled: () => {
        dispatch(setActiveSpatialAnalysisPanel(""));
      }
    });

  const [showSaveConfig, setShowSaveConfig] = useState<boolean>(false);

  const userPermissions = useGetUserPermission();
  const aiDatasetRecommendationPermission =
    userPermissions?.[EModuleName.AI_DATASET_RECOMMENDATION]?.[
      EModulePermission.VIEW
    ];
  const [isAIPermissionDialogOpen, setIsAIPermissionDialogOpen] =
    useState(false);

  const getSaveAsMenu = (): ButtonMenuItem[] => {
    return [
      {
        name: "Existing",
        onClick: () => {
          mutateSaveConfig({
            id: spatial.currentConfigId,
            name: spatial.title ?? "",
            description: spatial.description ?? ""
          });
        },
        hide: !spatial.currentConfigId
      },
      {
        name: "New Project",
        onClick: () => {
          dispatch(setActiveSpatialAnalysisPanel(""));
          setShowSaveConfig(true);
        }
      }
    ];
  };

  const handleShowProjectList = () => {
    if (showCatchmentPopup) dispatch(toggleCatchmentPopup(false));
    if (showAreaAdminPopup) dispatch(toggleAreaAdminPopup(false));
    if (showPolygonPopup) dispatch(setShowPolygonPopup(false));
    dispatch(toggleShowProjectList(true));
  };

  const checkIsSameValue = () => {
    const { _persist, ...rest } = spatial;
    return Object.keys(rest).every(
      (key) =>
        rest[key as keyof SpatialState] ===
        spatialInitialState[key as keyof SpatialState]
    );
  };

  const hasNoAdminAreaSettings = useMemo((): boolean => {
    const levels: Array<keyof AdminAreaSettings> = [
      "lv0",
      "lv1",
      "lv2",
      "lv3",
      "lv4"
    ];
    return levels.every((level) => !adminAreaSettings?.[level]);
  }, [adminAreaSettings]);

  const hasNoDatasets = useMemo((): boolean => {
    return Object.keys(datasets || {}).length === 0;
  }, [datasets]);

  const hasNotCatchmentSettings = useMemo((): boolean => {
    const { radiusData, catchmentType, placeCoordinates, roadAccessConfig } =
      catchmentSettings || {};

    const isRadiusDataEmpty =
      !radiusData || radiusData.every((val) => val.trim() === "");
    const isPlaceCoordinatesEmpty = (placeCoordinates?.length || 0) === 0;

    return (
      isRadiusDataEmpty &&
      catchmentType === "radius" &&
      isPlaceCoordinatesEmpty &&
      roadAccessConfig?.measurementType === "distance"
    );
  }, [catchmentSettings]);

  const hasNoSpatialConfig = useMemo((): boolean => {
    const {
      areaInput,
      outputType,
      gridType,
      gridResolution,
      h3Resolution,
      scoringOption,
      areaSize
    } = spatial.spatialConfig || {};

    return (
      areaInput === spatialInitialState.spatialConfig.areaInput &&
      outputType === spatialInitialState.spatialConfig.outputType &&
      gridType === spatialInitialState.spatialConfig.gridType &&
      gridResolution === spatialInitialState.spatialConfig.gridResolution &&
      h3Resolution === spatialInitialState.spatialConfig.h3Resolution &&
      scoringOption === spatialInitialState.spatialConfig.scoringOption &&
      areaSize === spatialInitialState.spatialConfig.areaSize
    );
  }, [spatial.spatialConfig]);

  const hasNoGlobalFilter = useMemo((): boolean => {
    return (
      globalFilter.selectedAreaIds.length === 0 &&
      globalFilter.adminAreaSettings === null
    );
  }, [globalFilter.selectedAreaIds, globalFilter.adminAreaSettings]);

  const resetDisable = useMemo(() => {
    return (
      hasNoAdminAreaSettings &&
      hasNoDatasets &&
      hasNotCatchmentSettings &&
      hasNoSpatialConfig &&
      hasNoGlobalFilter
    );
  }, [
    hasNoAdminAreaSettings,
    hasNoDatasets,
    hasNotCatchmentSettings,
    hasNoSpatialConfig,
    hasNoGlobalFilter
  ]);

  const handleReset = () => {
    if (
      !Object.keys(datasets)?.length &&
      checkIsSameValue() &&
      hasNoGlobalFilter
    )
      return;

    showConfirmation({
      title: "Reset Analysis?",
      message: <div className="text-center">{MESSAGE.CONFIRMATION_RESET}</div>,
      cancelLabel: "",
      confirmLabel: "Reset",
      onConfirm: () => {
        handleResetDatasetToHistoryDataset();
        deleteAllFeatures();
        dispatch(resetSpatialState());
        dispatch(resetDatasetState());
        handleCloseDetailView();
        setGenerateResultClicked(false);
        dispatch(resetGlobalFilter());
        dispatch(toggleAreaAdminPopup(false));
        dispatch(toggleGlobalFilterPopup(false));
      }
    });
  };

  const handleGoBack = () => {
    dispatch(toggleShowProjectList(false));
    // reset query cache to refetch project list from page 1
    queryClient.removeQueries({
      queryKey: ["config-list-infinite"]
    });
  };

  const handleSaveConfiguration = (data: ConfigurationFormData) => {
    mutateSaveConfig(data);
    handleChangeConfigInfo({
      id: data.id,
      projectDescription: data.description,
      projectName: data.name
    });
  };

  const convertMarkerToWkt = (
    marker: MarkerPinItem,
    index: number
  ): string[] => {
    try {
      if (catchmentSettings?.catchmentType === ECatchmentType.BY_RADIUS) {
        return geoJSONArrayToWKT(
          (catchmentSettings?.radiusData || []).map((radius) =>
            getRadiusPolygon([marker.lng, marker.lat], Number(radius) || 0)
          )
        );
      }

      return geoJSONArrayToWKT(
        (catchmentSettings?.roadAccessConfig?.isochroneData as IsochroneData[])[
          index
        ].data.flatMap((d) => d.features)
      );
    } catch {
      return [];
    }
  };

  const getInputData = (): Marker[] | AdminArea | CustomCatchment[] => {
    if (spatial.spatialConfig.areaInput?.value === AreaInputType.ADMIN_AREA) {
      if (isAdminTreeEnabled) {
        return {
          adm_area_ids: spatial.adminTreeSettings.selectedAreaIds
        };
      }

      // Get the lowest admin level from adminAreaSettings
      const lowestAdminLevel = Object.entries(adminAreaSettings || {})
        .filter(([key, value]) => key.startsWith("lv") && value)
        .sort(([keyA], [keyB]) => {
          // Sort by level number in descending order (lv5 before lv4, etc.)
          const levelA = Number.parseInt(keyA.substring(2));
          const levelB = Number.parseInt(keyB.substring(2));
          return levelB - levelA;
        })
        .map(([_, value]) => value)[0];

      return { id: lowestAdminLevel?.value || "" };
    } else if (
      spatial.spatialConfig.areaInput?.value === AreaInputType.POLYGON
    ) {
      return Object.values(polygonSettings).map((polygonSetting, index) => {
        return {
          name: polygonSetting.name || `polygon-${index}`,
          polygon: [geoJSONToWKT(polygonSetting.polygon) as string]
        };
      });
    }

    if (catchmentSettings?.catchmentInputBy === ECatchmentInputBy.BY_LOCATION) {
      return (
        catchmentSettings?.placeCoordinates?.map((marker, index) => {
          return {
            name: marker.locationName || `Marker #${index + 1}`,
            coordinate: {
              latitude: marker.lat,
              longitude: marker.lng
            },
            polygon: convertMarkerToWkt(marker, index)
          };
        }) || []
      );
    } else {
      const datasets = catchmentSettings?.datasetConfig?.datasets ?? {};

      const markers: MarkerPinItem[] = [];

      createMarkersFromDataset(
        Object.values(datasets),
        (label, coordinates) => {
          markers.push({
            id: uuidv4(),
            locationName: label,
            lat: coordinates[1],
            lng: coordinates[0]
          });
        }
      );

      return (
        markers?.map((marker, index) => {
          return {
            name: marker.locationName || `Marker #${index + 1}`,
            coordinate: {
              latitude: marker.lat,
              longitude: marker.lng
            },
            polygon: convertMarkerToWkt(marker, index)
          };
        }) || []
      );
    }
  };

  const datasetFilter = (
    filters: Record<string, DatasetFilter[]>,
    timezone?: Timezone
  ): Record<string, SpatialAnalysisDatasetFilter[]> => {
    const result: Record<string, SpatialAnalysisDatasetFilter[]> = {};

    for (const [datasetId, filterArray] of Object.entries(filters)) {
      const transformedFilters: SpatialAnalysisDatasetFilter[] = [];

      const filterTransformed = getDeepestAdminFilterFirst(filterArray);

      for (const filter of filterTransformed) {
        const {
          filterId: filterIdTemp,
          attributeCode = "",
          paired_key_attribute_id
        } = filter;
        const filterId = removeStartFromUnderscore(
          paired_key_attribute_id ?? filterIdTemp
        );

        transformedFilters.push({
          schema_id: filterId,
          filter: buildFilterAsDatasetPayload(filter, timezone),
          attribute_code: attributeCode
        });
      }
      result[datasetId] = transformedFilters;
    }
    return result;
  };

  const getDatasetAvailableStatus = async () => {
    const datasetIds = Object.keys(datasets);

    const datasetMetadata = await Promise.all(
      datasetIds.map(async (id) => {
        const baseId = id.includes("_") ? id.split("_")[0] : id;
        try {
          const res = await getDatasetMetadata(baseId);
          const published = res.published ?? false;
          dispatch(updateDatasetPublishedStatus({ datasetId: id, published }));
          return published;
        } catch (err) {
          console.error(err);
          dispatch(
            updateDatasetPublishedStatus({ datasetId: id, published: false })
          );
          return false;
        }
      })
    );

    return datasetMetadata;
  };

  const handleRunAnalysis = async () => {
    setGenerateResultClicked(true);

    const datasetAvailableStatus = (await getDatasetAvailableStatus()).some(
      (status) => !status
    );

    if (!canGenerateAnalysisResults || datasetAvailableStatus) return;

    const datasetFilterTransformed = datasetFilter(
      filters,
      userProfile?.timezone
    );

    const formattedDatasets = Object.keys(datasets).map((id) => {
      const datasetBaseId = id.includes("_") ? id.split("_")[0] : id;
      const currentDataset = datasets[id];
      const currentDatasetDisplayName =
        currentDataset.data.display_name || currentDataset.data.name;
      const datasetWeightAttributes = weightValues[id]?.attributes ?? {};

      return {
        id: datasetBaseId,
        filter: datasetFilterTransformed[id] ?? [],
        weight: Object.entries(datasetWeightAttributes).map(
          ([schemaId, value]) => {
            const negativeImpact = getNegativeImpactValue(value);
            const percentage = getPercentageValue(value);

            const resolvedWeightPrimitive =
              isNewSetupWeightOptionsEnabled && percentage !== undefined
                ? percentage
                : value;
            const parsedWeight = parseWeightValue(resolvedWeightPrimitive);

            return {
              schema_id: schemaId,
              weight: parsedWeight,
              ...(negativeImpact && {
                negative_impact: negativeImpact
              })
            };
          }
        ),
        display_name: currentDatasetDisplayName,
        grid_config: getGridConfig(currentDataset),
        group_by: currentDataset.group_by
      };
    });

    const configuration = {
      id: spatial.currentConfigId,
      name: spatial.title ?? "",
      description: spatial.description ?? "",
      adm_area_ids: mappingAdmAreaIds(),
      datasets: mappingDataset(),
      area_input: mappingAreaInput(),
      analysis_output: mappingAnalysisOutput()
    };

    const analysisConfig = {
      datasets: formattedDatasets,
      input: getInputData(),
      output: getOutputConfiguration(),
      configuration: JSON.stringify(configuration)
    };

    handleCloseDetailView();
    postDatasetAnalysis(analysisConfig);
  };

  const getOutputConfiguration = (): OutputSiteProfiling | OutputGrid => {
    const {
      outputType,
      gridType,
      gridResolution,
      h3Resolution,
      scoringOption
    } = spatial.spatialConfig;

    if (outputType === EOutputAnalysisType.GRID) {
      const isGeohash = gridType === EOutputGridType.GEOHASH;
      const resolutionValue = isGeohash
        ? Number(gridResolution?.value)
        : Number(h3Resolution?.value);

      return {
        output_type: AnalysisResultType.TYPE_GRID,
        grid_config: {
          grid_type: isGeohash
            ? AnalysisResultType.TYPE_GEOHASH
            : AnalysisResultType.TYPE_H3,
          level: resolutionValue
        },
        scoring_option: scoringOption as ScoringOptions
      };
    }

    return {
      output_type: AnalysisResultType.TYPE_SITE_PROFILING,
      scoring_option: scoringOption as ScoringOptions
    };
  };

  const isOutputValidationError =
    generateResultClicked && validationErrors?.outputAnalysisMessage !== null;

  const isResolutionValidationError =
    generateResultClicked && validationErrors?.resolutionMessage !== null;

  const isDatasetValidationError =
    generateResultClicked && validationErrors?.datasetMessage !== null;

  const handleChangeConfigInfo = (data: ProjectFormData) => {
    if (
      data.projectName === spatial.title &&
      data.projectDescription === spatial.description &&
      data.id === spatial.currentConfigId
    )
      return;
    dispatch(
      setSpatialState({
        ...spatial,
        currentConfigId: data.id,
        title: data.projectName,
        description: data.projectDescription
      })
    );
  };

  return (
    <div
      className={twMerge(
        "absolute top-[30px] left-[16px] z-[9] border-gainsboro bg-white rounded-lg w-[338px] max-h-[calc(100vh-54px)] overflow-hidden",
        "flex flex-col",
        Object.keys(datasets).length > 0 ? "" : "min-h-[224px]"
      )}
    >
      {showProjectList ? (
        <ProjectList onGoBack={handleGoBack} />
      ) : (
        <>
          {/* Header */}
          <SpatialHeader title="Analysis" onClose={onClose}>
            <div className="flex gap-3.5">
              <Tooltip content="Reset Analysis" position="bottom-center">
                <button
                  type="button"
                  data-testid="btn-reset"
                  className={cn(
                    "bg-transparent border-0 p-0 transition-colors",
                    {
                      "cursor-pointer fill-dim-gray": !resetDisable,
                      "cursor-not-allowed fill-light-gray": resetDisable
                    }
                  )}
                  onClick={handleReset}
                  disabled={resetDisable}
                  aria-disabled={resetDisable}
                  aria-label="Reset Analysis"
                >
                  <FilePlusIcon />
                </button>
              </Tooltip>
              <Tooltip content="Open Project" position="bottom-center">
                <button
                  className="bg-transparent border-0 p-0"
                  onClick={handleShowProjectList}
                  aria-label="Open Project"
                >
                  <FolderOpenIcon />
                </button>
              </Tooltip>
            </div>
          </SpatialHeader>
          {/* Content */}
          <div className="flex flex-col mt-3 custom-scrollbar overflow-y-auto pb-[64px]">
            {/* Project and description */}
            {spatial.currentConfigId && (
              <ProjectForm
                initialData={{
                  id: spatial.currentConfigId,
                  projectName: spatial.title ?? "",
                  projectDescription: spatial.description ?? ""
                }}
                onUpdate={handleChangeConfigInfo}
              />
            )}

            {/* Filter Area | Global Filter*/}
            {isGlobalFilterEnabled && (
              <>
                <GlobalFilter
                  onClearFilter={onClearGlobalFilter}
                  onOpenFilter={onOpenGlobalFilter}
                />
                <Divider className="bg-white-smoke border-none w-[100%] min-h-[8px] my-4 [&>div]:border-0" />
              </>
            )}

            {/* Data selection */}
            <div className="flex flex-col px-4">
              <span className="font-sans font-semibold text-txtHeading text-sm">
                Data Selection
              </span>
              <p className=" text-txtBody font-sans text-[10px]">
                Select your own data or explore our dataset library.
              </p>

              <DatasetList
                isValidationError={isDatasetValidationError}
                onOpenDatasetTabularModal={() =>
                  onOpenDatasetTabularModal(true)
                }
              />
              {generateResultClicked && validationErrors?.datasetMessage && (
                <p className="px-2 text-[10px] text-cinnabar self-start">
                  {validationErrors.datasetMessage}
                </p>
              )}

              <div className="flex flex-row items-center gap-2 mt-3 ">
                <Button
                  variant="secondary"
                  className="h-8 btn-secondary text-sm !font-bold flex-grow flex-shrink-0"
                  onClick={() => onOpenDatasetExplorer(true)}
                  data-testid={`${TEST_ID_PREFIX}-add-dataset-button`}
                >
                  <CirclePlusIcon className="fill-teal-500" />
                  <span>Add Dataset</span>
                </Button>

                {isDatasetRecommendationAIEnabled && (
                  <LokasiAIProvider
                    httpClient={axiosInstance}
                    topics={["dataset_recommendation"]}
                  >
                    <DatasetRecommendationAI
                      onClickDisabled={
                        aiDatasetRecommendationPermission
                          ? undefined
                          : () => setIsAIPermissionDialogOpen(true)
                      }
                    />
                  </LokasiAIProvider>
                )}
              </div>
            </div>

            <Divider className="bg-white-smoke border-none w-[100%] min-h-[8px] mt-4 [&>div]:border-0" />
            <CollapseView title="Spatial Settings">
              {/* Area Input */}
              <SpatialAreaInput
                onOpenCatchmentPopup={() =>
                  dispatch(setActiveSpatialAnalysisPanel("showCatchmentPopup"))
                }
                onOpenAreaAdminPopup={() => {
                  dispatch(setActiveSpatialAnalysisPanel("showAreaAdminPopup"));
                  if (isAdminTreeEnabled) {
                    dispatch(
                      setAdminTreeSettingsTempSelectedIds(
                        spatial.adminTreeSettings.selectedAreaIds
                      )
                    );
                    dispatch(
                      setAdminAreaSettingsDataTemp(
                        spatial.adminAreaSettingsData
                      )
                    );
                  }
                }}
                onOpenPolygonPopup={() => {
                  synchronizeFeatures();
                  dispatch(setPolygonSettingsTemp(polygonSettings));
                  dispatch(setActiveSpatialAnalysisPanel("showPolygonPopup"));
                }}
                isError={
                  generateResultClicked && !!validationErrors?.areaInputMessage
                }
              />
              {generateResultClicked && validationErrors?.areaInputMessage && (
                <p className="px-4 text-[10px] text-cinnabar self-start my-1">
                  {validationErrors.areaInputMessage}
                </p>
              )}
              {/* Output analysis */}
              <SpatialOutputAnalysis
                isOutputValidationError={isOutputValidationError}
                isResolutionValidationError={isResolutionValidationError}
              />
              <div className="flex px-4 my-2">
                <Button
                  variant="secondary"
                  className="h-[32px] w-full btn-secondary text-sm !font-bold"
                  onClick={() =>
                    dispatch(
                      setActiveSpatialAnalysisPanel(
                        "showDataWeightSettingsPopup"
                      )
                    )
                  }
                >
                  {spatial.spatialConfig.outputType ===
                  EOutputAnalysisType.GRID ? (
                    <span>Setting Data Weight</span>
                  ) : (
                    <span>Set Data Weight</span>
                  )}
                </Button>
              </div>
            </CollapseView>
            <Divider className="bg-white-smoke w-[100%] min-h-[8px] [&>div]:border-0" />
          </div>
          {/* Bottom button config */}
          <div className="flex px-4 gap-2 absolute bottom-0 bg-white w-full py-3 rounded-t-none rounded-lg">
            <ButtonMenu
              variant="secondary"
              title="Save as"
              className="w-1/2 !font-bold"
              menu={getSaveAsMenu()}
              disabled={!hasAvailableDataset}
              data-testid={`${TEST_ID_PREFIX}-save-as-button`}
            />
            <Button
              className="h-[40px] w-1/2 btn-primary text-sm !font-bold"
              aria-label="Generate Results"
              disabled={
                isAnalysisJobListEnabled
                  ? isLoadingPostAnalysis
                  : !!currRunningAnalysisId
              }
              onClick={handleRunAnalysis}
              data-testid={`${TEST_ID_PREFIX}-generate-results-button`}
            >
              <span>Generate Results</span>
            </Button>
          </div>
        </>
      )}
      {showSaveConfig && (
        <SaveConfigurationPopup
          show
          nameIsExist={projectNameExist}
          setNameIsExist={setProjectNameExist}
          onClose={() => setShowSaveConfig(false)}
          onSave={handleSaveConfiguration}
        />
      )}
      {isAIPermissionDialogOpen &&
        !aiDatasetRecommendationPermission &&
        isDatasetRecommendationAIEnabled &&
        createPortal(
          <UnlockPremiumModal
            title={MESSAGE.UNLOCK_AI_POPOVER_TITLE}
            description={MESSAGE.UNLOCK_AI_CONTENT_POPOVER_DESCRIPTION}
            contactUsDesc={
              MESSAGE.UNLOCK_AI_CONTENT_POPOVER_DESCRIPTION_CONTENT
            }
            onClose={() => setIsAIPermissionDialogOpen(false)}
            classNameContainer="z-[9999]"
          />,
          document.body
        )}
    </div>
  );
};

export default SpatialAnalysisPopup;
